<template>
  <div class="min-h-screen flex flex-col">
    <HeaderComponent />
    
    <main class="flex-1">
      <!-- Section principale -->
      <section class="mt-[70px] py-[50px] w-full flex justify-center items-center relative">
        <div class="container lg:max-w-[1200px] px-5 w-full relative flex justify-center items-center">
          <!-- Grille des offres -->
          <div class="w-full flex lg:flex-row flex-col-reverse justify-between items-center h-auto lg:h-[450px] gap-5">
            <!-- Offres secondaires (grille 2x2) -->
            <div class="lg:w-[calc(50%-10px)] w-full lg:h-full h-[300px] flex flex-wrap justify-between gap-4">
              <router-link
                v-for="(offre, index) in slides"
                :key="index"
                class="w-[calc(50%-10px)] h-[calc(50%-10px)] rounded-lg mb-4 relative overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 group"
                :to="'/boutique/produits/'+ offre.lotId"
              >
                <!-- Conteneur image avec fallback -->
                <div class="w-full h-full absolute top-0 left-0 flex justify-center items-center bg-gray-100 overflow-hidden">
                  <img 
                    :src="offre.photos[0] || defaultImage" 
                    :alt="offre.lotName" 
                    class="w-full h-full absolute object-cover transition-transform duration-500 group-hover:scale-110"
                    @error="handleImageError"
                  />
                  <!-- Image de fallback -->
                  <div v-if="imageError[index]" class="w-full h-full flex items-center justify-center bg-orange-100">
                    <span class="text-orange-500 text-lg font-medium">Image non disponible</span>
                  </div>
                </div>
                
                <!-- Overlay avec informations -->
                <div class="w-full h-full p-5 absolute top-0 left-0 flex flex-col justify-between items-start bg-black/40 hover:bg-black/50 transition-colors duration-300">
                  <div></div> <!-- Espaceur -->
                  <div>
                    <h1 class="uppercase text-white md:line-clamp-2 line-clamp-1 font-bold text-sm md:text-base">{{ offre.lotName }}</h1>
                    <p class="mt-1">
                      <span class="font-bold text-white">{{ formatPrice(offre.prix_reduit) }} <strong class="text-xs">FCFA</strong></span>
                      <span v-if="offre.prix !== offre.prix_reduit" class="ml-2 text-xs text-gray-200 font-bold line-through">
                        {{ formatPrice(offre.prix) }}F
                      </span>
                    </p>
                  </div>
                </div>
              </router-link>
            </div>
            
            <!-- Offre principale (carrousel) -->
            <div class="lg:w-[calc(50%-10px)] w-full lg:h-full h-[250px] flex justify-center items-center relative bg-orange-50 rounded-lg overflow-hidden">
              <swiper 
                :modules="modules" 
                :slides-per-view="1" 
                :loop="true" 
                :autoplay="{ delay: 3000, disableOnInteraction: false }" 
                :pagination="{ clickable: true }"
                class="w-full h-full"
              >
                <swiper-slide v-for="(offre, index) in slides" :key="'slide-'+index" class="w-full h-full relative">
                  <router-link class="w-full h-full relative overflow-hidden block" :to="'/boutique/produits/'+ offre.lotId">
                    <!-- Conteneur image avec fallback -->
                    <div class="w-full h-full flex justify-center items-center bg-gray-100">
                      <img 
                        :src="offre.photos[0] || defaultImage"
                        :alt="offre.lotName" 
                        class="w-full h-full object-cover"
                        @error="handleImageError"
                      />
                      <!-- Image de fallback -->
                      <div v-if="carouselImageError[index]" class="w-full h-full flex items-center justify-center bg-orange-100">
                        <span class="text-orange-500 text-xl font-medium">Image non disponible</span>
                      </div>
                    </div>
                    
                    <!-- Overlay avec informations -->
                    <div class="w-full h-full p-5 absolute top-0 left-0 flex flex-col justify-between items-start bg-black/40">
                      <div></div> <!-- Espaceur -->
                      <div>
                        <h1 class="uppercase text-white text-xl md:text-2xl line-clamp-2 font-bold">{{ offre.lotName }}</h1>
                        <p class="mt-2">
                          <span class="font-bold text-white text-lg">{{ formatPrice(offre.prix_reduit) }} <strong class="text-sm">FCFA</strong></span>
                          <span v-if="offre.prix !== offre.prix_reduit" class="ml-3 text-sm text-gray-200 font-bold line-through">
                            {{ formatPrice(offre.prix) }}F
                          </span>
                        </p>
                        <button class="mt-3 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md text-sm font-medium transition-colors duration-300">
                          Voir l'offre
                        </button>
                      </div>
                    </div>
                  </router-link>
                </swiper-slide>
              </swiper>
            </div>
          </div>
        </div>
      </section>
    </main>
    
    <FooterComponent />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import HeaderComponent from '../components/HeaderComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import { Pagination, A11y, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
const defaultImage = '/img/default.png'
const brokenImages = ref([]) 

// États
const occasionProduits = ref([]);
const slides = ref([]);
const imageError = ref({});
const carouselImageError = ref({});
const modules = [Autoplay, Pagination, A11y];

// Méthodes
const handleImageError = (event) => {
  event.target.src = defaultImage;
  if (produit.value.photos && produit.value.photos.includes(event.target.src)) {
    brokenImages.value.push(event.target.src);
    tryNextImage();
  }
};

const handleCarouselImageError = (index) => {
  carouselImageError.value[index] = true;
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price);
};

// Fetch des données
const fetchProduits = async () => {
  try {
    const response = await axios.get('/api/lots/produits/last4occasion');
    occasionProduits.value = response.data;
  } catch (err) {
    console.error('Erreur lors du chargement des produits:', err);
  }
};

const fetchProduitsVenu = async () => {
  try {
    const response = await axios.get('/api/lots/produits/venu');
    slides.value = response.data;
    // Initialiser les états d'erreur d'image
    imageError.value = slides.value.reduce((acc, _, index) => ({ ...acc, [index]: false }), {});
    carouselImageError.value = slides.value.reduce((acc, _, index) => ({ ...acc, [index]: false }), {});
  } catch (error) {
    console.error('Failed to fetch fetchProduitsVenu:', error);
    // Gestion d'erreur plus poussée possible ici (affichage à l'utilisateur)
  }
};

// Cycle de vie
onMounted(() => {
  fetchProduits();
  fetchProduitsVenu();
});
</script>

<style>
/* Animation au survol */
.router-link:hover .overlay-info {
  background-color: rgba(0, 0, 0, 0.5);
}

/* Style personnalisé pour la pagination du swiper */
.swiper-pagination-bullet-active {
  background-color: #f97316 !important; /* orange-500 */
}
</style>